import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, CheckSquare, History } from 'lucide-react'
import { Card, CardBody, CardHeader, Pagination, Tabs } from '@/components/ui'
import { PageHeader, QueryBoundary } from '@/components/domain'
import { ActionItemList } from '@/features/actions/components/ActionItemList'
import { useActionItems } from '@/features/actions/hooks'
import type { ActionListParams } from '@/features/actions/api'
import { MeetingListItem } from './components/MeetingListItem'
import { useMeetings } from './hooks'

type SeriesTabId = 'actions' | 'meetings'

const ALL_MEETINGS_PAGE_SIZE = 20
const ACTION_ITEMS_PAGE_SIZE = 20

export function SeriesDetailPage() {
  const { seriesId } = useParams<{ seriesId: string }>()
  // Only `items[0]` is read, for the page title -- this must stay decoupled
  // from the Occurrences tab's own paginated fetch below, or browsing to
  // page 2 there would change which meeting the title comes from.
  const { data, isPending, error, refetch } = useMeetings({ series: seriesId, pageSize: 1 })
  // Fetched here rather than inside SeriesActionItemsPanel so the page number
  // lives alongside the query it drives, both owned by the parent that
  // switches tabs.
  const [actionItemsPage, setActionItemsPage] = useState(1)
  const actionItemsParams = {
    series: seriesId, page: String(actionItemsPage), pageSize: String(ACTION_ITEMS_PAGE_SIZE),
  }
  const actionItems = useActionItems(actionItemsParams)
  const [tab, setTab] = useState<SeriesTabId>('meetings')
  const title = data?.items[0]?.title ?? 'Series'

  return (
    <div>
      <QueryBoundary isLoading={isPending} error={error} onRetry={refetch}>
        <PageHeader
          eyebrow={
            <Link
              to="/meetings"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted hover:text-fg"
            >
              <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
              All meetings
            </Link>
          }
          title={title}
          below={
            <Tabs
              activeId={tab}
              onChange={(id) => setTab(id as SeriesTabId)}
              tabs={[
                { id: 'actions', label: 'Action items', icon: <CheckSquare /> },
                { id: 'meetings', label: 'Occurrences', icon: <History /> },
              ]}
            />
          }
        />

        {tab === 'actions' && seriesId && (
          <SeriesActionItemsPanel
            actionItems={actionItems}
            params={actionItemsParams}
            page={actionItemsPage}
            onPageChange={setActionItemsPage}
          />
        )}
        {tab === 'meetings' && seriesId && <OccurrencesPanel seriesId={seriesId} />}
      </QueryBoundary>
    </div>
  )
}

function SeriesActionItemsPanel(
  { actionItems, params, page, onPageChange }: {
    actionItems: ReturnType<typeof useActionItems>
    params: ActionListParams
    page: number
    onPageChange: (page: number) => void
  },
) {
  const { data, isPending, error, refetch } = actionItems

  return (
    <QueryBoundary isLoading={isPending} error={error} onRetry={refetch}>
      <div className="space-y-4">
        <ActionItemList items={data?.items ?? []} context="workspace" filterParams={params} />
        {data && data.total > ACTION_ITEMS_PAGE_SIZE && (
          <Pagination
            page={page}
            pageSize={ACTION_ITEMS_PAGE_SIZE}
            total={data.total}
            onPageChange={onPageChange}
          />
        )}
      </div>
    </QueryBoundary>
  )
}

function OccurrencesPanel({ seriesId }: { seriesId: string }) {
  const [page, setPage] = useState(1)
  const { data, isPending, error, refetch } = useMeetings({
    series: seriesId, page, pageSize: ALL_MEETINGS_PAGE_SIZE,
  })
  const items = data?.items ?? []

  return (
    <QueryBoundary isLoading={isPending} error={error} onRetry={refetch}>
      <div className="space-y-4">
        <Card>
          <CardHeader title="Occurrences" />
          <CardBody className="p-0">
            {items.length === 0 ? (
              <p className="px-4 py-3 text-sm text-fg-subtle">No meetings found</p>
            ) : (
              <ul>
                {items.map((m) => (
                  // Every row here is already known to belong to this series,
                  // so the "Recurring" badge (useful on the All Meetings list,
                  // where it distinguishes recurring meetings from one-offs)
                  // would just repeat what the page title already says.
                  <MeetingListItem key={m.id} meeting={m} showRecurringBadge={false} />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
        {data && data.total > ALL_MEETINGS_PAGE_SIZE && (
          <Pagination
            page={page}
            pageSize={ALL_MEETINGS_PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        )}
      </div>
    </QueryBoundary>
  )
}
